import { subMilliseconds, format } from 'date-fns';
import { merge, isFunction } from 'lodash';
import { api } from '@/api/common/instances';
import { comma, kilobytes } from '@/utils/formatter';
import { getClosest } from '@/utils/datetime';
import motletable from '@/mixins/motletable';
import chartable from '@/mixins/chartable';
import LineChart from '../../../components/LineChart';

export default {
  components: {
    LineChart,
  },
  mixins: [motletable, chartable],
  props: {
    sstmId: {
      type: String,
      required: true,
    },
    storeName: {
      type: String,
      required: true,
    },
    datetime: {
      type: Date,
      required: true,
    },
    activeIncrement: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      data: [],
      motletOptions: {
        monitor: {
          systemId: this.sstmId,
        },
        descendant: {
          systemId: this.sstmId,
        },
      },
    };
  },
  computed: {
    settings() {
      if (this.data.length === 0) {
        return {};
      }
      const percent = this.chartField.type == 'PERCENT';
      const yValue = item => (item[this.chartField.code] === '' || item[this.chartField.code] === undefined ? '' : item[this.chartField.code] * this.chartField.unit);

      const xAxis = {
        data: this.data.map(({ ctime }) => ctime),
        axisLabel: {
          interval: Math.ceil(this.data.length / 6),
        },
      };
      const yAxis = {
        name: `${this.chartField.text} ${percent ? '(%)' : ''}`,
        max: percent ? 100 : undefined,
        maxInterval: percent ? 25 : undefined,
        splitNumber: percent ? 4 : undefined,
      };
      const series = [
        {
          type: 'line',
          id: this.sstmId,
          lineStyle: {
            color: this.descendants.length == 1 ? this.descendants[0].color : this.chartColor.merge,
          },
          data: this.data.map((item, index) => [index, yValue(item), item.ctime]),
        },
      ];
      const valueFormatter = (data, type = this.chartField.type) => {
        const formatted = {
          unit: '',
          data: '',
        };
        const isEmpty = data === undefined || data === null || data === '';
        if (type == 'PERCENT') {
          formatted.unit = isEmpty ? '' : ' %';
          formatted.data = data;
        } else if (type == 'DIGIT') {
          formatted.data = comma(data);
        } else if (type == 'KILO_BYTE') {
          formatted.data = kilobytes(data);
        } else {
          formatted.data = data;
        }
        return `${formatted.data}${formatted.unit}`;
      };
      const formatter = {
        tooltip: params => {
          const param = params[0];
          const contents = [];
          const color = this.descendants.length == 1 ? this.descendants[0].color : this.chartColor.merge;
          contents.push('<i class="v-icon notranslate mdi mdi-clock-time-four-outline mr-1" style="font-size: 12px; line-height: 10px"></i>');
          contents.push(format(parseInt(param.data[2]), 'yyyy-MM-dd HH:mm:ss'));
          contents.push('<br />');
          contents.push(this.circle(color));
          contents.push(`<span style="color:${color};font-weight:900;">`);
          contents.push(param.seriesId);
          contents.push('</span> : ');
          contents.push(valueFormatter(param.data[1]));
          return contents.join('');
        },
      };
      const defaults = {
        chart: {
          xAxis,
          yAxis,
          series,
        },
        formatter,
        systemId: this.sstmId,
        // Added.by.KWShin.2024.08.15.
        datetime: this.datetime,
      };
      return merge({}, defaults, isFunction(this.getSettings) ? this.getSettings() : undefined);
    },
  },
  watch: {
    'monitor.cycle': {
      handler() {
        this.fetchData();
      },
    },
    sstmId: {
      handler(value) {
        this.setDescendants(value);
        this.fetchData();
      },
    },
    datetime: {
      handler() {
        this.fetchData();
      },
    },
    activeIncrement: {
      immediate: true,
      handler(value) {
        if (value === false) this.fetchData();
      },
    },
  },
  methods: {
    async fetchData() {
      if (this.activeIncrement) return;
      const bounds = this.getBounds();
      if (!bounds) return;
      const params = {
        ...bounds,
        sstmId: this.sstmId,
        motletCode: this.monitor.monitorCode,
        ...this.dataParam,
      };
      ({
        data: [{ data: this.data }],
      } = await api.get('/motlet-data/chart', { params }));
    },
    // BEGIN::Modified.by.KWShin.2024.08.15.
    // AS IS
    //  - TimeNavigator의 기준시각이 차트의 X축상의 마지막 지점이 되도록 조회하였다.
    //  - 외부 화면에서 연결이 되어 들어올 때는 URL Parameter상의 기준시각을
    //    TimeNavigator의 기준시각과 일치시키고 X축상의 중앙 지점이 되도록 조회하였다.
    // TO BE
    //  - 무조건 TimeNavigator의 기준시각이 X축상의 3/4 지점이 되도록 조회한다.
    // ------------------------------------------------------------------------
    // getBounds() {
    //   if (!this.monitor) return null;
    //   let endTime = getClosest(this.datetime, this.monitor.cycle);
    //   let beginYmdhms = subMilliseconds(endTime, this.monitor.cycle * (this.monitor.points - 1)).getTime();
    //   let endYmdhms = endTime.getTime();

    //   const datetime = parseInt(this.$route.query.t ?? 0);
    //   if (datetime) {
    //     const addableTimestamp = parseInt((endYmdhms - beginYmdhms) / 2);
    //     endTime = getClosest(new Date(endYmdhms + addableTimestamp), this.monitor.cycle);
    //     beginYmdhms = subMilliseconds(endTime, this.monitor.cycle * (this.monitor.points - 1)).getTime();
    //     endYmdhms = endTime.getTime();
    //   }
    //   return {
    //     beginYmdhms,
    //     endYmdhms,
    //   };
    // },
    // ------------------------------------------------------------------------
    getBounds() {
      if (!this.monitor) return null;
      let endTime = getClosest(this.datetime, this.monitor.cycle);
      let quater = Math.round(this.monitor.points / 4);
      endTime = new Date(endTime.getTime() + quater * this.monitor.cycle);
      let beginYmdhms = subMilliseconds(endTime, this.monitor.cycle * (this.monitor.points - 1)).getTime();
      let endYmdhms = endTime.getTime();

      return {
        beginYmdhms,
        endYmdhms,
      };
    },
    // FINISH::Modified.by.KWShin.2024.08.15.
  },
};
